"use client";
import React, { ComponentProps, FC, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { componentNameConstants, dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import ListWithShowMore from "../../components/News/components/List";
import type { IFilters, IStockItem } from "../../interfaces/StockList.interface";
import type { StockListingUILabels } from "../../interfaces/StockListingTableAEM.interface";
import {
  areStockFiltersEqual,
  createDefaultStockFilters,
  normalizeQueryStringEncoding,
  parseStockFiltersFromSearchParams,
  restrictStockFilterCategoriesByKeys,
  restrictStockFiltersByKeys,
  resolveStockListingHeaderContent,
  updateSearchParamsWithStockFilters,
} from "./utils/stockListingHelpers";
import { normalizeBoolean } from "../../utils/normalizeBoolean";
import { fetchStockFilters } from "../../utils/stockListApi";
import StockActiveFilterChips from "./components/StockActiveFilterChips";
import StockFilters from "./components/StockFilters";
import StockHeader from "./components/StockHeader";
import StockListingFiltersSidebar from "./components/StockListingFiltersSidebar";
import StockListingPageSkeleton from "./components/StockListingPageSkeleton";
import StockTable from "./components/StockTable";
import type { SortOption } from "./components/StockSortDropdown";
import type { NormalizedFilterCategory } from "./context/StockListingFiltersContext";
import { StockListingFiltersProvider } from "./context/StockListingFiltersContext";
import { useStockListing } from "./hooks/useStockListing";

function hasAnySelectedStockFilters(filters?: IFilters): boolean {
  const f = filters;
  if (!f) return false;
  return (
    (f.index?.length ?? 0) > 0 ||
    (f.sector?.length ?? 0) > 0 ||
    (f.mktcap?.length ?? 0) > 0 ||
    (f.alphabetic?.length ?? 0) > 0
  );
}

export interface StockListingPageClientProps {
  /** Pre-fetched stock list (server-side) */
  initialStockList: IStockItem[];
  /** Total count of stocks matching the query */
  totalCount: number;
  /** Current sort value from search params */
  sortValue: string;
  /** Optional API sort column (e.g. "mktCap" for stock-for-new-investors) */
  sortField?: string;
  /** Entity type identifier (e.g. "topgainers", "toplosers") */
  entity: string;
  /** Exchange ID */
  exchId?: number;
  /** Limit per page */
  limit?: number;
  /** Whether more results are available */
  initialShowMore?: boolean;
  /** AEM content for the table */
  tableContent?: {
    title?: string | null;
    headerText?: string | null;
    entityType?: string | null;
    showDisclaimer?: boolean | string;
    showFilter?: boolean | string;
    sortOptions?: SortOption[];
    watchlist?: {
      modalTitle?: string;
      modalBtnCta?: string;
      successMsg?: string;
      errorMsg?: string;
      cta_text?: string;
      section_title?: string;
    };
    tableLabels?: {
      companyName?: string;
      ltp?: string;
    };
    uiLabels?: StockListingUILabels;
  };
  /** AEM version/locale */
  version?: string;
  /** Whether running inside the mobile app shell */
  isMobileApp?: boolean;
  /** Filter component to render in sidebar */
  filterComponent?: React.ReactNode;
  /** Render stock listing sidebar filters from client-only component */
  enableFiltersSidebar?: boolean;
  /** Hide Sort section inside mobile Filters drawer */
  hideMobileSortInFiltersDrawer?: boolean;
  /** Server-fetched filter categories for SSR */
  initialFilterCategories?: NormalizedFilterCategory[];
  /** Server-fetched active filters derived from URL query params */
  initialFilters?: IFilters;
  /** Server-rendered mobile header shell passed from StockListingPageServer. */
  mobileHeaderSlot?: React.ReactNode;
  /** Restrict available filters to provided filter keys (e.g. only mktcap). */
  allowedFilterKeys?: readonly (keyof IFilters)[];
  /** Optional override for Show More/Less button padding (page-specific). */
  paginationButtonClassName?: string;
}

const StockListingPageClient: FC<StockListingPageClientProps> = ({
  initialStockList,
  sortValue = 'asec',
  sortField,
  entity,
  exchId = 0,
  limit = 20,
  initialShowMore = false,
  tableContent,
  filterComponent,
  enableFiltersSidebar = false,
  hideMobileSortInFiltersDrawer = false,
  initialFilterCategories,
  initialFilters,
  mobileHeaderSlot,
  allowedFilterKeys,
  paginationButtonClassName,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [activeSortValue, setActiveSortValue] = useState(sortValue);
  const [activeFilters, setActiveFilters] = useState<IFilters>(() =>
    restrictStockFiltersByKeys(
      initialFilters ??
        parseStockFiltersFromSearchParams(
          new URLSearchParams(searchParams.toString()),
        ),
      allowedFilterKeys,
    ),
  );
  const [filterCategories, setFilterCategories] = useState<NormalizedFilterCategory[]>(
    () =>
      restrictStockFilterCategoriesByKeys(
        initialFilterCategories ?? [],
        allowedFilterKeys,
      ),
  );
  const [filtersMetaLoading, setFiltersMetaLoading] = useState(false);

  const serializedSearchParams = searchParams.toString();

  useEffect(() => {
    setActiveSortValue(sortValue);
  }, [sortValue]);

  // Sync filters with URL only on pages that actually show filter controls.
  const shouldSyncFilterUrl = enableFiltersSidebar || Boolean(filterComponent);

  useEffect(() => {
    if (!shouldSyncFilterUrl) {
      return;
    }
    const fromUrl = parseStockFiltersFromSearchParams(
      new URLSearchParams(serializedSearchParams),
    );
    const scopedFromUrl = restrictStockFiltersByKeys(fromUrl, allowedFilterKeys);
    setActiveFilters((prev) =>
      areStockFiltersEqual(prev, scopedFromUrl) ? prev : scopedFromUrl,
    );
  }, [allowedFilterKeys, serializedSearchParams]);

  useEffect(() => {
    setFilterCategories(
      restrictStockFilterCategoriesByKeys(
        initialFilterCategories ?? [],
        allowedFilterKeys,
      ),
    );
  }, [allowedFilterKeys, initialFilterCategories]);

  const effectiveFilterComponent = useMemo(
    () => (enableFiltersSidebar ? <StockListingFiltersSidebar /> : filterComponent),
    [enableFiltersSidebar, filterComponent],
  );
  const syncFilterMetadata = Boolean(effectiveFilterComponent);
  const scopedActiveFilters = useMemo(
    () => restrictStockFiltersByKeys(activeFilters, allowedFilterKeys),
    [activeFilters, allowedFilterKeys],
  );

  useEffect(() => {
    if (!syncFilterMetadata) {
      return;
    }
    // Filters metadata should not be re-fetched on every apply/clear.
    // It is already server-fetched via `initialFilterCategories` when `filterComponent` is provided.
    // We only fetch here when we don't have any initial categories (e.g. prop missing/empty).
    if ((initialFilterCategories?.length ?? 0) > 0) {
      return;
    }

    let cancelled = false;
    setFiltersMetaLoading(true);
    void fetchStockFilters()
      .then((next) => {
        if (!cancelled) {
          setFilterCategories(
            restrictStockFilterCategoriesByKeys(next, allowedFilterKeys),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFiltersMetaLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [syncFilterMetadata, initialFilterCategories, allowedFilterKeys]);

  let activeSortField = sortField;
  if (tableContent?.entityType === "stockfornewinvestors") {
    activeSortField =
      activeSortValue === tableContent.sortOptions?.[0]?.value
        ? "mktCap"
        : "perChange";
  }

  const {
    stocks,
    isLoading,
    isLoadingMore,
    showMore,
    handleShowMore: handleShowMoreBase,
  } = useStockListing({
    initialStocks: initialStockList,
    entity,
    exchId,
    sort: activeSortValue,
    sortField: activeSortField,
    filters: scopedActiveFilters,
    limit,
    initialShowMore,
  });

  useEffect(() => {
    if (!shouldSyncFilterUrl) {
      return;
    }
    const nextParams = updateSearchParamsWithStockFilters(
      new URLSearchParams(serializedSearchParams),
      scopedActiveFilters,
    );

    const currentParams = normalizeQueryStringEncoding(serializedSearchParams);
    const serializedNext = normalizeQueryStringEncoding(nextParams.toString());
    if (serializedNext !== currentParams) {
      router.replace(serializedNext ? `?${serializedNext}` : pathname, { scroll: false });
    }
  }, [pathname, router, scopedActiveFilters, serializedSearchParams, shouldSyncFilterUrl]);

  const showDisclaimer = normalizeBoolean(tableContent?.showDisclaimer, true);
  const hasActiveFilters = hasAnySelectedStockFilters(scopedActiveFilters);
  // If a concrete filterComponent is provided (e.g., All Stocks filters),
  // always show the filter sidebar on desktop, regardless of AEM flag.
  const showFilterFromAem = normalizeBoolean(tableContent?.showFilter, false);
  const showFilter = effectiveFilterComponent ? true : showFilterFromAem;

  const { title, shouldRenderHeader, shouldRenderHeaderBar } =
    resolveStockListingHeaderContent({
      tableContent,
    });
  const entityType = tableContent?.entityType ?? null;
  const uiLabels = tableContent?.uiLabels ?? ({} as StockListingUILabels);
  const tableLabels = tableContent?.tableLabels;

  const handleShowMore = useCallback(() => {
    const ctaText = uiLabels.paginationShowMoreText ?? "Show more";
    const dlData = {
      component: componentNameConstants.STOCK_LIST_CARD,
      event: dlEventConstants.CLICK,
      event_type: dlEventConstants.BUTTON_CLICK,
      element: dlElementConstants.PLP_CLICKED,
      cta_text: ctaText,
    };
    const cleverTapObj = {
      EP_STOCK_NAME: ctaText,
      EP_PAGE_NAME: title ?? "",
      EP_CTA: ctaText,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_STOCK_PLP,
    };
    dataLayer("", dlData, cleverTapObj);
    handleShowMoreBase();
  }, [handleShowMoreBase, title, uiLabels.paginationShowMoreText]);

  return (
    <StockListingFiltersProvider
      activeFilters={activeFilters}
      setActiveFilters={(updater) => {
        setActiveFilters((prev) => {
          const next =
            typeof updater === "function"
              ? (updater as (prevState: IFilters) => IFilters)(prev)
              : updater;
          return restrictStockFiltersByKeys(
            next ?? createDefaultStockFilters(),
            allowedFilterKeys,
          );
        });
      }}
      filterCategories={filterCategories}
      isLoadingFilters={syncFilterMetadata && filtersMetaLoading}
      activeSortValue={activeSortValue}
      setActiveSortValue={setActiveSortValue}
      uiLabels={uiLabels}
    >
      {/* Main content wrapper with left filter sidebar */}
      <div className="flex w-full">
        {/* Left filter sidebar */}
        <StockFilters showFilter={showFilter} filterComponent={effectiveFilterComponent} uiLabels={uiLabels} />

        {/* Main content section */}
        <section
          className="box-border block w-full max-w-[892px] md:border-l-3 border-l-[rgba(0,0,0,0.08)] bg-white leading-[19.2px] md:p-3"
          aria-label={
            shouldRenderHeader
              ? title ?? (uiLabels.stockListingAriaLabel ?? "Stock listing")
              : (uiLabels.stockListingAriaLabel ?? "Stock listing")
          }
        >
          {/* Page header with title and disclaimer */}
          {shouldRenderHeader && (
            <StockHeader
              title={title ?? ""}
              showDisclaimer={showDisclaimer}
              disclaimerText={uiLabels.disclaimerText}
            />
          )}

          {/* Active filter chips below header */}
          {effectiveFilterComponent && <StockActiveFilterChips uiLabels={uiLabels} />}

          {/* Mobile header bar with filters drawer */}
          {shouldRenderHeaderBar && mobileHeaderSlot}

          {/* Stock table wrapped with Show More/Less */}
          <ListWithShowMore
            showMore={showMore}
            onShowMore={handleShowMore}
            containerClassName=""
            buttonContainerClassName="p-4 pt-0"
            showMoreText={uiLabels.paginationShowMoreText}
            showLessText={uiLabels.paginationShowLessText}
            paginationButtonClassName={paginationButtonClassName}
          >
            <StockTable
              data={stocks}
              entityType={entityType}
              isLoading={isLoading || isLoadingMore}
              isListUpdating={isLoading && stocks.length > 0}
              showHeaderTopBorder={!!effectiveFilterComponent}
              tableLabels={tableLabels}
              uiLabels={uiLabels}
              pageTitle={title}
              watchlistProps={{
                modalTitle: tableContent?.watchlist?.modalTitle,
                modalBtnCta: tableContent?.watchlist?.modalBtnCta,
                successMsg: tableContent?.watchlist?.successMsg,
                errorMsg: tableContent?.watchlist?.errorMsg,
                cta_text: tableContent?.watchlist?.cta_text,
                section_title: tableContent?.watchlist?.section_title,
              }}
              hasActiveFilters={hasActiveFilters}
            />
            {/* Loading skeletons for pagination */}
            {isLoadingMore && stocks.length > 0 && (
              <div className="flex flex-col gap-2 mt-2">
                {[1, 2, 3].map((num) => (
                  <BFLSkeleton key={`pagination-skeleton-${num}`} height={60} classes="rounded-lg" />
                ))}
              </div>
            )}
          </ListWithShowMore>
        </section>
      </div>
    </StockListingFiltersProvider>
  );
};

// useSearchParams() requires a Suspense boundary on statically prerendered routes.
const SuspendedStockListingPageClient = (
  props: ComponentProps<typeof StockListingPageClient>,
) => {
  const { title, shouldRenderHeader, shouldRenderHeaderBar } = resolveStockListingHeaderContent({
    tableContent: props.tableContent,
  });
  const showDisclaimer = normalizeBoolean(props.tableContent?.showDisclaimer, true);

  return (
    <Suspense
      fallback={
        <StockListingPageSkeleton
          rowCount={props.initialStockList?.length ?? props.limit ?? 10}
          headerSlot={
            shouldRenderHeader ? (
              <StockHeader
                title={title ?? ""}
                showDisclaimer={showDisclaimer}
                disclaimerText={props.tableContent?.uiLabels?.disclaimerText}
              />
            ) : undefined
          }
          mobileHeaderSlot={shouldRenderHeaderBar ? props.mobileHeaderSlot : undefined}
          showMore={props.initialShowMore}
        />
      }
    >
      <StockListingPageClient {...props} />
    </Suspense>
  );
};

export default SuspendedStockListingPageClient;
